import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { http, formatApiError } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { PageHeader, Panel } from "@/components/admin/PageBits";
import { BrandMark } from "@/components/portal/PortalShell";

const FIELDS = [
  ["org_name", "Nome da organização operadora"],
  ["partner_brand", "Marca parceira (exibida somente se autorizada)"],
  ["logo_url", "URL do logotipo (HTTPS)"],
  ["operator_document", "CNPJ / identificação do operador"],
  ["operator_contact", "Contato do operador (e-mail / telefone)"],
];

const Row = ({ label, children }) => (
  <div><label className="mb-1 block text-xs text-slate-400">{label}</label>{children}</div>
);

const inputCls = "border-white/10 bg-black/30 text-slate-100";

export default function BrandingPage() {
  const { reload } = useBranding();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get("/admin/settings").then(({ data }) => setS(data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!s) return null;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setSlot = (i, label) => set("contact_slots", s.contact_slots.map((x, j) => (j === i ? { ...x, label } : x)));

  const save = async () => {
    setBusy(true);
    const keys = ["org_name", "partner_brand", "brand_authorized", "logo_url", "primary_color", "background_color", "operator_document", "operator_contact", "demo_mode", "auto_score_enabled", "auto_score_points", "retention_days", "contact_slots"];
    try {
      const { data } = await http.put("/admin/settings", Object.fromEntries(keys.map((k) => [k, ["retention_days", "auto_score_points"].includes(k) ? Number(s[k]) : s[k]])));
      setS(data);
      reload();
      toast.success("Configurações salvas.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Marca e atendimento" subtitle="Configurações · identidade do operador, cores, horários de contato e retenção de dados.">
        <button onClick={save} disabled={busy} className="btn-gold h-9 px-4 text-sm" data-testid="save-branding-btn"><Save className="h-4 w-4" /> Salvar</button>
      </PageHeader>
      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Identidade">
          <div className="space-y-4">
            {FIELDS.map(([k, label]) => (
              <Row key={k} label={label}><Input value={s[k] || ""} onChange={(e) => set(k, e.target.value)} className={inputCls} data-testid={`branding-${k}`} /></Row>
            ))}
            <label className="flex items-center justify-between rounded-md border border-white/10 p-3 text-sm">
              <span>Uso da marca parceira autorizado</span>
              <Switch checked={s.brand_authorized} onCheckedChange={(v) => set("brand_authorized", v)} data-testid="branding-brand-authorized" />
            </label>
            <div className="grid grid-cols-2 gap-4">
              {[["primary_color", "Cor institucional"], ["background_color", "Cor de fundo"]].map(([k, label]) => (
                <Row key={k} label={label}>
                  <div className="flex items-center gap-2">
                    <input type="color" value={s[k]} onChange={(e) => set(k, e.target.value.toUpperCase())} className="h-10 w-12 cursor-pointer rounded border border-white/10 bg-transparent" data-testid={`branding-${k}-picker`} />
                    <Input value={s[k]} onChange={(e) => set(k, e.target.value)} className={`${inputCls} font-mono-x`} data-testid={`branding-${k}`} />
                  </div>
                </Row>
              ))}
            </div>
            <div className="flex items-center gap-3 rounded-md border border-white/10 p-3"><BrandMark config={s} size="sm" /><span className="text-sm text-slate-400">Pré-visualização do logotipo</span></div>
          </div>
        </Panel>
        <div className="space-y-6">
          <Panel title="Horários de contato" action={<button onClick={() => set("contact_slots", [...s.contact_slots, { label: "" }])} className="btn-ghost-sm" data-testid="slot-add-btn"><Plus className="h-3.5 w-3.5" /> Adicionar</button>}>
            <div className="space-y-2">
              {s.contact_slots.map((sl, i) => (
                <div key={sl.id || `new-${i}`} className="flex gap-2">
                  <Input value={sl.label} onChange={(e) => setSlot(i, e.target.value)} className={inputCls} data-testid={`slot-input-${i}`} />
                  <button disabled={s.contact_slots.length === 1} onClick={() => set("contact_slots", s.contact_slots.filter((_, j) => j !== i))} className="icon-btn disabled:opacity-30" data-testid={`slot-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="Pontuação e dados">
            <div className="space-y-3 rounded-md border border-white/10 p-3" data-testid="auto-score-section">
              <label className="flex items-center justify-between text-sm">
                <span>Envio automático quando nenhum operador estiver online</span>
                <Switch checked={s.auto_score_enabled} onCheckedChange={(v) => set("auto_score_enabled", v)} data-testid="branding-auto-score-enabled" />
              </label>
              <Row label="Pontos pré-programados">
                <Input type="number" min={0} value={s.auto_score_points} onChange={(e) => set("auto_score_points", e.target.value)} disabled={!s.auto_score_enabled} className={`${inputCls} w-48 font-mono-x`} data-testid="branding-auto-score-points" />
              </Row>
              <p className="text-xs text-slate-500">Sem nenhum painel aberto, o cliente recebe esta pontuação após alguns segundos e segue direto para o agendamento.</p>
            </div>
            <p className="mt-2 text-xs text-slate-500">Fonte oficial: {s.integration_available ? "configurada" : "não configurada (defina REWARDS_OFFICIAL_API_URL e implemente o provedor)"}.</p>
            <Row label="Retenção de dados (dias, 30–3650)">
              <Input type="number" min={30} max={3650} value={s.retention_days} onChange={(e) => set("retention_days", e.target.value)} className={`${inputCls} mt-1 w-40`} data-testid="branding-retention-days" />
            </Row>
          </Panel>
        </div>
      </div>
    </div>
  );
}
